import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Clock, Folder } from 'lucide-react'
import {
  EntityContextMenu,
  type EntityContextMenuEntry,
} from '@/components/features/entity-context-menu'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { SidebarTreeRow } from '@/components/layout/sidebar/SidebarTreeRow'
import { fetchWithAuth } from '@/lib/api/core'
import {
  OpenDestinationIcons,
  resolveOpenExternalIcon,
} from '@/lib/open-destination-icons'
import { testIds } from '@/lib/test-ids'
import type { DriveBrowseScopeKind } from './drive-browse-events'
import { uniqueDriveItemsById, type DriveBrowseItem } from './drive-tree-adapter'
import {
  driveExternalFolderUrl,
  driveExternalRootUrl,
  jumpFolderBrowseQuery,
  jumpToOpenFolder,
  type DriveFolderJump,
  type DriveJumpFolder,
} from './drive-folder-jump'

export type { DriveFolderJump }

const ROOT_PAD = 20
const CHILD_PAD = 32
const DriveOpenIcon = resolveOpenExternalIcon(GoogleDriveIcon)

function isFolderItem(item: DriveBrowseItem): boolean {
  return Boolean(
    item.isFolder || item.mimeType === 'application/vnd.google-apps.folder',
  )
}

function mapChildFolders(
  items: DriveBrowseItem[],
  parent: DriveJumpFolder,
): DriveJumpFolder[] {
  return uniqueDriveItemsById(items ?? [])
    .filter((item) => isFolderItem(item) && item.id !== parent.id)
    .map((item) => ({
      id: item.id,
      name: item.name,
      webViewLink: item.webViewLink,
      driveId: parent.driveId,
      driveName: parent.driveName,
      parentId: parent.id,
    }))
}

function DriveJumpRow({
  padLeft,
  active,
  icon,
  label,
  chevron,
  onOpen,
  onOpenExternal,
}: {
  padLeft: number
  active: boolean
  icon: ReactNode
  label: string
  chevron?: { expanded: boolean; onClick: () => void }
  onOpen: () => void
  onOpenExternal: () => void
}) {
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null)
  const entries: EntityContextMenuEntry[] = [
    {
      label: 'Open',
      icon: OpenDestinationIcons.open,
      onSelect: onOpen,
    },
    {
      label: 'Open in Drive',
      icon: DriveOpenIcon,
      onSelect: onOpenExternal,
    },
  ]

  return (
    <>
      <SidebarTreeRow
        padLeft={padLeft}
        active={active}
        icon={icon}
        label={label}
        className="cursor-pointer"
        chevron={
          chevron
            ? {
                expanded: chevron.expanded,
                onClick: () => chevron.onClick(),
              }
            : undefined
        }
        onClick={onOpen}
        onContextMenu={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setMenu({ x: event.clientX, y: event.clientY })
        }}
      />
      {menu ? (
        <EntityContextMenu
          x={menu.x}
          y={menu.y}
          entries={entries}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </>
  )
}

export function DriveFolderJumpTree({
  workspaceId,
  currentFolderId,
  currentViewKind,
  currentDriveId,
  onJump,
}: {
  workspaceId: string
  currentFolderId?: string
  currentViewKind: DriveBrowseScopeKind | 'sharedDrive'
  currentDriveId?: string
  onJump: (jump: DriveFolderJump) => void
}) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(['myDrive']))
  const [myDriveFolders, setMyDriveFolders] = useState<DriveJumpFolder[]>([])
  const [childFolders, setChildFolders] = useState<Record<string, DriveJumpFolder[]>>({})
  const [sharedDrives, setSharedDrives] = useState<DriveJumpFolder[]>([])
  const loadedFolderIds = useRef(new Set<string>())

  const toggle = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const loadMyDriveRoot = useCallback(async () => {
    if (!workspaceId) return
    try {
      const params = new URLSearchParams({
        organizationId: workspaceId,
        corpus: 'myDrive',
        foldersOnly: '1',
      })
      const res = await fetchWithAuth(`/api/google-drive/browse?${params.toString()}`)
      if (!res.ok) return
      const data = (await res.json()) as { items?: DriveBrowseItem[] }
      setMyDriveFolders(
        (data.items ?? []).filter(isFolderItem).map((item) => ({
          id: item.id,
          name: item.name,
          webViewLink: item.webViewLink,
        })),
      )
    } catch {
      setMyDriveFolders([])
    }
  }, [workspaceId])

  const loadSharedDrives = useCallback(async () => {
    if (!workspaceId) return
    try {
      const res = await fetchWithAuth(
        `/api/google-drive/drives?organizationId=${encodeURIComponent(workspaceId)}`,
      )
      if (!res.ok) return
      const data = (await res.json()) as { drives?: Array<{ id: string; name: string }> }
      setSharedDrives(
        (data.drives ?? []).map((drive) => ({
          id: drive.id,
          name: drive.name,
          driveId: drive.id,
          driveName: drive.name,
          webViewLink: driveExternalFolderUrl(drive.id),
        })),
      )
    } catch {
      setSharedDrives([])
    }
  }, [workspaceId])

  const loadChildFolders = useCallback(
    async (folder: DriveJumpFolder) => {
      if (!workspaceId || loadedFolderIds.current.has(folder.id)) return
      loadedFolderIds.current.add(folder.id)
      try {
        const res = await fetchWithAuth(
          `/api/google-drive/browse?${jumpFolderBrowseQuery(workspaceId, folder).toString()}`,
        )
        if (!res.ok) {
          loadedFolderIds.current.delete(folder.id)
          return
        }
        const data = (await res.json()) as { items?: DriveBrowseItem[] }
        setChildFolders((prev) => ({
          ...prev,
          [folder.id]: mapChildFolders(data.items ?? [], folder),
        }))
      } catch {
        loadedFolderIds.current.delete(folder.id)
        setChildFolders((prev) => ({ ...prev, [folder.id]: [] }))
      }
    },
    [workspaceId],
  )

  useEffect(() => {
    loadedFolderIds.current.clear()
    setChildFolders({})
    setMyDriveFolders([])
    setSharedDrives([])
    void loadMyDriveRoot()
    void loadSharedDrives()
  }, [loadMyDriveRoot, loadSharedDrives])

  const openExternal = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  const expandIfNeeded = (folder: DriveJumpFolder) => {
    if (expanded.has(folder.id)) return
    toggle(folder.id)
    void loadChildFolders(folder)
  }

  const renderFolderRow = (folder: DriveJumpFolder, padLeft: number) => {
    const isOpen = expanded.has(folder.id)
    const kids = childFolders[folder.id]
    const isSharedRoot = Boolean(folder.driveId && folder.id === folder.driveId)
    const active = isSharedRoot
      ? currentViewKind === 'sharedDrive' &&
        currentDriveId === folder.id &&
        !currentFolderId
      : folder.driveId
        ? currentViewKind === 'sharedDrive' &&
          currentDriveId === folder.driveId &&
          currentFolderId === folder.id
        : currentViewKind === 'myDrive' && currentFolderId === folder.id

    return (
      <div key={folder.id}>
        <DriveJumpRow
          padLeft={padLeft}
          active={active}
          icon={<Folder className="h-3.5 w-3.5 text-yellow-500/80" />}
          label={folder.name}
          chevron={{
            expanded: isOpen,
            onClick: () => {
              toggle(folder.id)
              if (!isOpen) void loadChildFolders(folder)
            },
          }}
          onOpen={() => {
            onJump(jumpToOpenFolder(folder))
            expandIfNeeded(folder)
          }}
          onOpenExternal={() =>
            openExternal(folder.webViewLink || driveExternalFolderUrl(folder.id))
          }
        />
        {isOpen
          ? (kids ?? []).map((child) => renderFolderRow(child, padLeft + 12))
          : null}
      </div>
    )
  }

  const myDriveOpen = expanded.has('myDrive')
  const sharedOpen = expanded.has('sharedDrives')

  return (
    <div className="space-y-0.5" role="tree" data-testid={testIds.driveSidebar.folderTree}>
      <p className="px-5 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        Folders
      </p>
      <DriveJumpRow
        padLeft={ROOT_PAD}
        active={currentViewKind === 'myDrive' && !currentFolderId}
        icon={<Folder className="h-3.5 w-3.5 text-yellow-500/80" />}
        label="My Drive"
        chevron={{
          expanded: myDriveOpen,
          onClick: () => toggle('myDrive'),
        }}
        onOpen={() => {
          onJump({ viewKind: 'myDrive' })
          if (!myDriveOpen) toggle('myDrive')
        }}
        onOpenExternal={() => openExternal(driveExternalRootUrl('myDrive'))}
      />
      {myDriveOpen
        ? myDriveFolders.map((folder) => renderFolderRow(folder, CHILD_PAD))
        : null}

      <DriveJumpRow
        padLeft={ROOT_PAD}
        active={
          (currentViewKind === 'sharedDrives' || currentViewKind === 'sharedDrive') &&
          !currentDriveId
        }
        icon={<Folder className="h-3.5 w-3.5 text-yellow-500/80" />}
        label="Shared Drives"
        chevron={{
          expanded: sharedOpen,
          onClick: () => toggle('sharedDrives'),
        }}
        onOpen={() => {
          onJump({ viewKind: 'sharedDrives' })
          if (!sharedOpen) toggle('sharedDrives')
        }}
        onOpenExternal={() => openExternal(driveExternalRootUrl('sharedDrives'))}
      />
      {sharedOpen
        ? sharedDrives.map((drive) => renderFolderRow(drive, CHILD_PAD))
        : null}

      <DriveJumpRow
        padLeft={ROOT_PAD}
        active={currentViewKind === 'recent'}
        icon={<Clock className="h-3.5 w-3.5 text-muted-foreground" />}
        label="Recent"
        onOpen={() => onJump({ viewKind: 'recent' })}
        onOpenExternal={() => openExternal(driveExternalRootUrl('recent'))}
      />
    </div>
  )
}
